// frontend/src/pages/CamDoBrainNews.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import camdobrainService from '../services/camdobrainService';
import Loading from '../components/Loading';
import './CamDoBrainNews.css';

const CamDoBrainNews = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const brandProfileIdParam = searchParams.get('brand_profile_id');

  // State
  const [loading, setLoading] = useState(true);
  const [fetching, setFetching] = useState(false);
  const [brandProfiles, setBrandProfiles] = useState([]);
  const [selectedBrandId, setSelectedBrandId] = useState(null);
  const [news, setNews] = useState([]);
  const [headlines, setHeadlines] = useState([]);
  const [topics, setTopics] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedCountry, setSelectedCountry] = useState('CL');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [activeTab, setActiveTab] = useState('brand'); // brand, search, headlines
  const [error, setError] = useState(null);
  const [selectedPeriod, setSelectedPeriod] = useState('week'); // today, week, last_week, month, three_months

  // Opciones de período
  const periodOptions = [
    { value: 'today', label: 'Hoy', days: 1 },
    { value: 'week', label: 'Esta semana', days: 7 },
    { value: 'last_week', label: 'Semana pasada', days: 14 },
    { value: 'month', label: '1 mes', days: 30 },
    { value: 'three_months', label: '3 meses', days: 90 },
  ];

  // News Config State
  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [savingConfig, setSavingConfig] = useState(false);
  const [newsConfig, setNewsConfig] = useState({
    custom_keywords: [],
    include_competitors: true,
    include_industry: true,
    include_trends: true,
    countries: ['CL', 'US']
  });
  const [newKeyword, setNewKeyword] = useState('');

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Load news and config when brand changes
  useEffect(() => {
    if (selectedBrandId) {
      loadBrandNews(selectedBrandId);
      loadNewsConfig(selectedBrandId);
    }
  }, [selectedBrandId]);

  // Reload news when period changes
  useEffect(() => {
    if (selectedBrandId) {
      loadBrandNews(selectedBrandId);
    }
  }, [selectedPeriod]);

  const loadInitialData = async () => {
    try {
      setLoading(true);

      // Load brand profiles and topics in parallel
      const [profilesRes, topicsRes] = await Promise.all([
        camdobrainService.getBrandProfiles(),
        camdobrainService.getNewsTopics(),
      ]);

      const profiles = Array.isArray(profilesRes.data)
        ? profilesRes.data
        : (profilesRes.data?.results || []);
      setBrandProfiles(profiles);

      setTopics(topicsRes.data.topics || []);
      setCountries(topicsRes.data.countries || []);

      if (brandProfileIdParam && profiles.find(p => p.id === parseInt(brandProfileIdParam))) {
        setSelectedBrandId(parseInt(brandProfileIdParam));
      } else if (profiles.length > 0) {
        setSelectedBrandId(profiles[0].id);
      }

      // Load headlines
      loadHeadlines('CL');
    } catch (err) {
      console.error('Error loading data:', err);
      setError('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  // Función auxiliar para filtrar artículos por período
  const filterByPeriod = (articles) => {
    if (!articles || articles.length === 0) return [];

    const period = periodOptions.find(p => p.value === selectedPeriod);
    const days = period ? period.days : 7;
    const now = new Date();

    if (selectedPeriod === 'last_week') {
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
      const twoWeeksAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      return articles.filter(article => {
        const articleDate = new Date(article.published_at || article.fetched_at);
        return articleDate >= twoWeeksAgo && articleDate < oneWeekAgo;
      });
    }

    const sinceDate = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    return articles.filter(article => {
      const articleDate = new Date(article.published_at || article.fetched_at);
      return articleDate >= sinceDate;
    });
  };

  const loadBrandNews = async (brandId) => {
    try {
      // Obtener días según el período seleccionado
      const period = periodOptions.find(p => p.value === selectedPeriod);
      const days = period ? period.days : 7;

      const res = await camdobrainService.getRecentNews(brandId, days);
      const newsData = Array.isArray(res.data) ? res.data : [];

      // Aplicar filtro de período (necesario para "semana pasada")
      setNews(filterByPeriod(newsData));
    } catch (err) {
      console.error('Error loading brand news:', err);
      setNews([]);
    }
  };

  const loadNewsConfig = async (brandId) => {
    try {
      const res = await camdobrainService.getNewsConfig(brandId);
      if (res.data.news_config) {
        setNewsConfig({
          custom_keywords: res.data.news_config.custom_keywords || [],
          include_competitors: res.data.news_config.include_competitors ?? true,
          include_industry: res.data.news_config.include_industry ?? true,
          include_trends: res.data.news_config.include_trends ?? true,
          countries: res.data.news_config.countries || ['CL', 'US']
        });
      }
    } catch (err) {
      console.error('Error loading news config:', err);
    }
  };

  const handleSaveConfig = async () => {
    if (!selectedBrandId) return;

    try {
      setSavingConfig(true);
      await camdobrainService.updateNewsConfig(selectedBrandId, newsConfig);
      setShowConfigPanel(false);
    } catch (err) {
      console.error('Error saving config:', err);
      setError('Error al guardar configuración');
    } finally {
      setSavingConfig(false);
    }
  };

  const handleAddKeyword = () => {
    if (newKeyword.trim() && !newsConfig.custom_keywords.includes(newKeyword.trim())) {
      setNewsConfig({
        ...newsConfig,
        custom_keywords: [...newsConfig.custom_keywords, newKeyword.trim()]
      });
      setNewKeyword('');
    }
  };

  const handleRemoveKeyword = (keyword) => {
    setNewsConfig({
      ...newsConfig,
      custom_keywords: newsConfig.custom_keywords.filter(k => k !== keyword)
    });
  };

  const handleToggleCountry = (countryCode) => {
    const currentCountries = newsConfig.countries || [];
    if (currentCountries.includes(countryCode)) {
      setNewsConfig({
        ...newsConfig,
        countries: currentCountries.filter(c => c !== countryCode)
      });
    } else {
      setNewsConfig({
        ...newsConfig,
        countries: [...currentCountries, countryCode]
      });
    }
  };

  const loadHeadlines = async (country) => {
    try {
      const res = await camdobrainService.getHeadlines(country, 20);
      setHeadlines(res.data.articles || []);
    } catch (err) {
      console.error('Error loading headlines:', err);
    }
  };

  const handleFetchNews = async () => {
    if (!selectedBrandId) return;

    try {
      setFetching(true);
      setError(null);
      await camdobrainService.fetchNews(selectedBrandId, 15);
      await loadBrandNews(selectedBrandId);
    } catch (err) {
      console.error('Error fetching news:', err);
      setError(err.response?.data?.error || 'Error al obtener noticias');
    } finally {
      setFetching(false);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    try {
      setLoading(true);
      const res = await camdobrainService.searchNews(searchQuery, selectedCountry, 100);
      const articles = res.data.articles || [];
      // Aplicar filtro de período
      setSearchResults(filterByPeriod(articles));
      setActiveTab('search');
    } catch (err) {
      console.error('Error searching news:', err);
      setError('Error en la busqueda');
    } finally {
      setLoading(false);
    }
  };

  const handleTopicChange = async (topic) => {
    setSelectedTopic(topic);
    if (!topic) return;

    try {
      setLoading(true);
      const res = await camdobrainService.getTopicNews(topic, selectedCountry, 100);
      const articles = res.data.articles || [];
      // Aplicar filtro de período
      setSearchResults(filterByPeriod(articles));
      setActiveTab('search');
    } catch (err) {
      console.error('Error loading topic news:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCountryChange = async (country) => {
    setSelectedCountry(country);
    loadHeadlines(country);
  };

  const formatTimeAgo = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);

    if (days > 0) return `hace ${days}d`;
    if (hours > 0) return `hace ${hours}h`;
    return 'hace un momento';
  };

  const renderNewsCard = (article, index) => (
    <div key={article.article_id || index} className="news-card">
      <div className="news-card-header">
        <span className="news-source">{article.source}</span>
        <span className="news-time">{article.time_ago || formatTimeAgo(article.published_at)}</span>
      </div>
      <h4 className="news-title">
        <a href={article.link} target="_blank" rel="noopener noreferrer">
          {article.title}
        </a>
      </h4>
      {(() => {
        const desc = (article.description || '').trim();
        const title = (article.title || '').trim();
        // No mostrar si la descripción es igual o empieza igual que el título
        const isSameAsTitle = title.length > 0 && (
          desc === title ||
          desc.startsWith(title.substring(0, Math.min(title.length, 60))) ||
          title.startsWith(desc.substring(0, Math.min(desc.length, 60)))
        );
        if (!desc || isSameAsTitle) return null;
        return <p className="news-description">{desc.substring(0, 180)}{desc.length > 180 ? '…' : ''}</p>;
      })()}
      {article.topic && (
        <span className={`news-topic topic-${article.topic.toLowerCase()}`}>
          {article.topic}
        </span>
      )}
    </div>
  );

  if (loading && !news.length && !headlines.length) {
    return <Loading />;
  }

  return (
    <div className="news-page">
      {/* Header */}
      <div className="news-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate('/camdobrain')}>
            ← Volver
          </button>
          <h1>Noticias Relevantes</h1>
        </div>
        <div className="header-actions">
          <select
            className="brand-select"
            value={selectedBrandId || ''}
            onChange={(e) => setSelectedBrandId(parseInt(e.target.value))}
          >
            {brandProfiles.map(profile => (
              <option key={profile.id} value={profile.id}>
                {profile.brand_name}
              </option>
            ))}
          </select>
          <select
            className="period-select"
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
          >
            {periodOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <button
            className="fetch-btn"
            onClick={handleFetchNews}
            disabled={fetching || !selectedBrandId}
          >
            {fetching ? 'Obteniendo...' : 'Actualizar Noticias'}
          </button>
          <button
            className="config-btn"
            onClick={() => setShowConfigPanel(!showConfigPanel)}
          >
            ⚙️ Configurar Keywords
          </button>
        </div>
      </div>

      {/* Config Panel */}
      {showConfigPanel && (
        <div className="config-panel">
          <div className="config-header">
            <h3>Configuración de Búsqueda de Noticias</h3>
            <button className="close-btn" onClick={() => setShowConfigPanel(false)}>×</button>
          </div>

          <div className="config-content">
            {/* Custom Keywords */}
            <div className="config-section">
              <h4>Keywords Personalizados</h4>
              <p className="config-description">Agrega palabras clave específicas para buscar noticias relevantes</p>
              <div className="keyword-input-row">
                <input
                  type="text"
                  placeholder="Agregar keyword..."
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleAddKeyword()}
                />
                <button onClick={handleAddKeyword}>Agregar</button>
              </div>
              <div className="keywords-list">
                {newsConfig.custom_keywords.map((keyword, index) => (
                  <span key={index} className="keyword-tag">
                    {keyword}
                    <button onClick={() => handleRemoveKeyword(keyword)}>×</button>
                  </span>
                ))}
                {newsConfig.custom_keywords.length === 0 && (
                  <span className="no-keywords">No hay keywords personalizados</span>
                )}
              </div>
            </div>

            {/* Auto-generated Keywords */}
            <div className="config-section">
              <h4>Keywords Automáticos</h4>
              <p className="config-description">Selecciona qué fuentes usar para generar keywords automáticamente</p>
              <div className="checkbox-group">
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={newsConfig.include_competitors}
                    onChange={(e) => setNewsConfig({ ...newsConfig, include_competitors: e.target.checked })}
                  />
                  <span className="checkbox-label">
                    <strong>Competencia</strong>
                    <small>Incluir nombres de competidores configurados</small>
                  </span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={newsConfig.include_industry}
                    onChange={(e) => setNewsConfig({ ...newsConfig, include_industry: e.target.checked })}
                  />
                  <span className="checkbox-label">
                    <strong>Industria</strong>
                    <small>Incluir keywords relacionados con tu industria</small>
                  </span>
                </label>
                <label className="checkbox-item">
                  <input
                    type="checkbox"
                    checked={newsConfig.include_trends}
                    onChange={(e) => setNewsConfig({ ...newsConfig, include_trends: e.target.checked })}
                  />
                  <span className="checkbox-label">
                    <strong>Tendencias</strong>
                    <small>Incluir tendencias actuales de Google Trends</small>
                  </span>
                </label>
              </div>
            </div>

            {/* Countries Selection */}
            <div className="config-section">
              <h4>Países para Búsqueda</h4>
              <p className="config-description">Selecciona los países donde buscar noticias</p>
              <div className="countries-grid">
                {countries.map(country => (
                  <label key={country.code} className="country-checkbox">
                    <input
                      type="checkbox"
                      checked={(newsConfig.countries || []).includes(country.code)}
                      onChange={() => handleToggleCountry(country.code)}
                    />
                    <span>{country.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="config-footer">
            <button className="cancel-btn" onClick={() => setShowConfigPanel(false)}>
              Cancelar
            </button>
            <button
              className="save-btn"
              onClick={handleSaveConfig}
              disabled={savingConfig}
            >
              {savingConfig ? 'Guardando...' : 'Guardar Configuración'}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="error-banner">{error}</div>
      )}

      {/* Search Bar */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Buscar noticias..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <select
            value={selectedCountry}
            onChange={(e) => handleCountryChange(e.target.value)}
            className="country-select"
          >
            {countries.map(country => (
              <option key={country.code} value={country.code}>
                {country.name}
              </option>
            ))}
          </select>
          <button type="submit" className="search-btn">Buscar</button>
        </form>
        <div className="topic-filters">
          <button
            className={`topic-btn ${!selectedTopic ? 'active' : ''}`}
            onClick={() => { setSelectedTopic(''); setActiveTab('brand'); }}
          >
            Mi Marca
          </button>
          {topics.map(topic => (
            <button
              key={topic.id}
              className={`topic-btn ${selectedTopic === topic.id ? 'active' : ''}`}
              onClick={() => handleTopicChange(topic.id)}
            >
              {topic.name}
            </button>
          ))}
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs-container">
        <button
          className={`tab ${activeTab === 'brand' ? 'active' : ''}`}
          onClick={() => setActiveTab('brand')}
        >
          Noticias de mi Marca ({news.length})
        </button>
        <button
          className={`tab ${activeTab === 'headlines' ? 'active' : ''}`}
          onClick={() => setActiveTab('headlines')}
        >
          Titulares ({headlines.length})
        </button>
        <button
          className={`tab ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          Busqueda ({searchResults.length})
        </button>
      </div>

      {/* Content */}
      <div className="news-content">
        {activeTab === 'brand' && (
          <>
            {news.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📰</div>
                <h2>No hay noticias guardadas</h2>
                <p>Haz clic en "Actualizar Noticias" para obtener noticias relevantes para tu marca</p>
                <button onClick={handleFetchNews} disabled={fetching}>
                  {fetching ? 'Obteniendo...' : 'Obtener Noticias'}
                </button>
              </div>
            ) : (
              <div className="news-grid">
                {news.map((article, index) => renderNewsCard(article, index))}
              </div>
            )}
          </>
        )}

        {activeTab === 'headlines' && (
          <div className="news-grid">
            {headlines.map((article, index) => renderNewsCard(article, index))}
          </div>
        )}

        {activeTab === 'search' && (
          <>
            {searchResults.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">🔍</div>
                <h2>Sin resultados</h2>
                <p>Usa el buscador o selecciona un topico para encontrar noticias</p>
              </div>
            ) : (
              <div className="news-grid">
                {searchResults.map((article, index) => renderNewsCard(article, index))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default CamDoBrainNews;
